import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode, ComponentType } from 'react'
import { useLocalStorage } from './useLocalStorage'

type FeatureFlags = Record<string, boolean>
type GlobalOverride = boolean | null

interface FeatureFlagsContextValue {
	flags: FeatureFlags
	globalOverride: GlobalOverride
	enableFeature: (featureName: string) => void
	disableFeature: (featureName: string) => void
	toggleFeature: (featureName: string) => void
	setFeatures: (newFlags: Partial<FeatureFlags>) => void
	enableAll: () => void
	disableAll: () => void
	clearGlobalOverride: () => void
	isEnabled: (featureName: string) => boolean
}

interface FeatureFlagsProviderProps {
	children: ReactNode
	initialFlags?: Partial<FeatureFlags>
}

interface FeatureGateProps {
	feature: string
	children: ReactNode
	fallback?: ReactNode
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(undefined)

// Typed hook usage
// const isEnabled: boolean = useFeatureFlag('myFeature')
//
// Typed context access
// const controls: FeatureFlagsContextValue = useFeatureFlags()
//
// Generic HOC with type preservation
// const Enhanced = withFeatureGate<MyComponentProps>(
//   MyComponent,
//   'featureName',
//   FallbackComponent
// )
export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children, initialFlags }) => {
	const [flags, setFlags] = useState<FeatureFlags>({
		redeemOffers: false,
		// The series workspace page (ENG-2955). On by default; turning it off brings back the old series page, which
		// stays until that page is retired.
		seriesWorkspace: true,
		...initialFlags,
	})

	const [globalOverride, setGlobalOverride] = useLocalStorage('featureFlags:globalOverride', null)

	const enableFeature = useCallback((featureName: string): void => {
		setFlags((prev: FeatureFlags) => ({ ...prev, [featureName]: true }))
	}, [])

	const disableFeature = useCallback((featureName: string): void => {
		setFlags((prev: FeatureFlags) => ({ ...prev, [featureName]: false }))
	}, [])

	const toggleFeature = useCallback((featureName: string): void => {
		setFlags((prev: FeatureFlags) => ({ ...prev, [featureName]: !prev[featureName] }))
	}, [])

	const setFeatures = useCallback((newFlags: Partial<FeatureFlags>): void => {
		setFlags((prev: FeatureFlags) => ({ ...prev, ...(newFlags as FeatureFlags) }))
	}, [])

	const enableAll = useCallback((): void => {
		setGlobalOverride(true)
		// Reload the page to apply changes
		window.location.reload()
	}, [setGlobalOverride])

	const disableAll = useCallback((): void => {
		setGlobalOverride(false)
		// Reload the page to apply changes
		window.location.reload()
	}, [setGlobalOverride])

	const clearGlobalOverride = useCallback((): void => {
		setGlobalOverride(null)
		// Reload the page to apply changes
		window.location.reload()
	}, [setGlobalOverride])

	const isEnabled = useCallback(
		(featureName: string): boolean => {
			if (globalOverride !== null) return globalOverride
			return flags[featureName] || false
		},
		[flags, globalOverride]
	)

	const value: FeatureFlagsContextValue = useMemo(
		() => ({
			flags,
			globalOverride,
			enableFeature,
			disableFeature,
			toggleFeature,
			setFeatures,
			enableAll,
			disableAll,
			clearGlobalOverride,
			isEnabled,
		}),
		[
			flags,
			globalOverride,
			enableFeature,
			disableFeature,
			toggleFeature,
			setFeatures,
			enableAll,
			disableAll,
			clearGlobalOverride,
			isEnabled,
		]
	)

	return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>
}

export const useFeatureFlag = (featureName: string): boolean => {
	const context = useContext(FeatureFlagsContext)

	if (!context) {
		throw new Error('useFeatureFlag must be used within a FeatureFlagsProvider')
	}

	return context.isEnabled(featureName)
}

export const useFeatureFlags = (): FeatureFlagsContextValue => {
	const context = useContext(FeatureFlagsContext)

	if (!context) {
		throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider')
	}

	return context
}

export const FeatureGate: React.FC<FeatureGateProps> = ({ feature, children, fallback = null }) => {
	const isEnabled: boolean = useFeatureFlag(feature)
	return <>{isEnabled ? children : fallback}</>
}

export const withFeatureGate = <P extends object>(
	Component: ComponentType<P>,
	featureName: string,
	FallbackComponent: ComponentType<P> = () => null
) => {
	return (props: P): JSX.Element => {
		const isEnabled: boolean = useFeatureFlag(featureName)
		return isEnabled ? <Component {...props} /> : <FallbackComponent {...props} />
	}
}
